import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './DashboardHeader.css';
import NotificationModal from './NotificationModal';
import AuthPromptModal from './AuthPromptModal';
// import { io } from 'socket.io-client'; // Temporarily disabled
import { FaBell, FaShoppingCart, FaUser, FaBars, FaTimes, FaSearch } from 'react-icons/fa';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

function DashboardHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef();
  const searchDropdownRef = useRef();
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const fetchNotes = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${process.env.REACT_APP_API}/api/users/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotifications(res.data || []);
      } catch (err) {
        // ignore
      }
    };
    fetchNotes();
    // Poll for new notifications every 20 seconds
    const id = setInterval(fetchNotes, 20000);
    return () => clearInterval(id);
  }, [token]);

  // Fetch cart count
  useEffect(() => {
    const fetchCartCount = async () => {
      if (!token) {
        setCartCount(0);
        return;
      }
      try {
        const res = await axios.get(`${process.env.REACT_APP_API}/api/cart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const totalItems = res.data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
        setCartCount(totalItems);
      } catch (err) {
        setCartCount(0);
      }
    };
    fetchCartCount();

    // Listen for cart update events
    const handleCartUpdate = () => {
      fetchCartCount();
    };
    window.addEventListener('cartUpdated', handleCartUpdate);

    // Poll for cart updates every 30 seconds
    const id = setInterval(fetchCartCount, 30000);
    return () => {
      clearInterval(id);
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, [token]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = async (notif) => {
    try {
      await axios.patch(`${process.env.REACT_APP_API}/api/users/notifications/${notif._id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n._id === notif._id ? { ...n, read: true } : n));
      
      // If it's a message reply notification, fetch full message details and open modal
      if (notif.type === 'message_reply') {
        console.log('Message reply notification:', notif);
        setSelectedMessageNotif(notif);
        setShowMessageModal(true);
        setShowDropdown(false);
        
        if (notif.meta?.messageId) {
          setLoadingMessageDetails(true);
          try {
            console.log('Fetching message ID:', notif.meta.messageId);
            const res = await axios.get(`${process.env.REACT_APP_API}/api/customer-messages/${notif.meta.messageId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Message details response:', res.data);
            setMessageDetails(res.data);
            setLoadingMessageDetails(false);
          } catch (err) {
            console.error('Error fetching message details:', err);
            console.error('Error response:', err.response?.data);
            setMessageDetails(null);
            setLoadingMessageDetails(false);
          }
        } else {
          console.log('No messageId in notification meta');
        }
      }
    } catch (err) {
      // ignore
    }
  };

  // Socket.IO real-time notifications - TEMPORARILY DISABLED
  useEffect(() => {
    // Disabled to prevent CORS errors flooding console
    // if (!token) return;
    // let socket;
    // try {
    //   socket = io(process.env.REACT_APP_API.replace(/\/$/, ''), { auth: { token } });
    //   socket.on('notification', (n) => {
    //     // Prepend incoming notification
    //     setNotifications(prev => [n, ...prev]);
    //   });
    // } catch (err) {
    //   // ignore
    // }
    // return () => {
    //   if (socket && socket.disconnect) socket.disconnect();
    // };
  }, [token]);

  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalPayload, setModalPayload] = React.useState({ title: '', body: '', meta: null });
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedMessageNotif, setSelectedMessageNotif] = useState(null);
  const [messageDetails, setMessageDetails] = useState(null);
  const [loadingMessageDetails, setLoadingMessageDetails] = useState(false);

  // Handle body scroll lock for message modal
  useEffect(() => {
    if (showMessageModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMessageModal]);

  const handleLogout = () => {
    // Clear all auth data
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('isAdmin');
    setToken(null);
    
    // Use replace to prevent back button navigation
    navigate('/login', { replace: true });
    
    // Clear browser cache
    if (window.history && window.history.pushState) {
      window.history.pushState(null, '', window.location.href);
    }
  };

  useEffect(() => {
    // listen to storage changes (login/logout in other tabs)
    const onStorage = () => setToken(localStorage.getItem('token'));
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Search products as user types
  useEffect(() => {
    const searchProducts = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        setShowSearchDropdown(false);
        return;
      }

      try {
        const res = await axios.get(`${process.env.REACT_APP_API}/api/products?search=${encodeURIComponent(searchQuery.trim())}&limit=8`);
        setSearchResults(res.data.products || []);
        setShowSearchDropdown(true);
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
      }
    };

    const debounceTimer = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/catalog?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchDropdown(false);
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
    setSearchQuery('');
    setShowSearchDropdown(false);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="dashboard-header">
      <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
        {mobileMenuOpen ? <FaTimes /> : <FaBars />}
      </button>

      <div className="logo">
        <Link to="/home" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
          <img src="/images/chiqui_logo.svg" alt="Chiqui's Garden Logo" style={{ width: '32px', height: '32px' }} />
          <span className="logo-text">Chiqui's Garden</span>
        </Link>
      </div>

      <AnimatePresence mode="wait">
        {mobileMenuOpen && (
          <motion.nav 
            className="nav-links mobile-open"
            initial={{ opacity: 0, y: -10 }}
            animate={{ 
              opacity: 1, 
              y: 0,
              transition: { 
                duration: 0.25,
                ease: [0.4, 0, 0.2, 1]
              }
            }}
            exit={{ 
              opacity: 0, 
              y: -10,
              transition: { 
                duration: 0.2,
                ease: [0.4, 0, 0.2, 1]
              }
            }}
          >
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2, delay: 0.05 }}>
              <Link to="/catalog" onClick={() => setMobileMenuOpen(false)} className={isActive('/catalog') ? 'active' : ''}>Shop</Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2, delay: 0.08 }}>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className={isActive('/about') ? 'active' : ''}>About Us</Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2, delay: 0.11 }}>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className={isActive('/contact') ? 'active' : ''}>Contact</Link>
            </motion.div>
            {token && (
              <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2, delay: 0.14 }}>
                <Link to="/orders" onClick={() => setMobileMenuOpen(false)} className={isActive('/orders') ? 'active' : ''}>Orders</Link>
              </motion.div>
            )}
            
            {token && (
              <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2, delay: 0.17 }}>
                <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className={`mobile-profile-link ${isActive('/profile') ? 'active' : ''}`}>
                  Profile
                </Link>
              </motion.div>
            )}
            
            <motion.div className="mobile-auth-button" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2, delay: 0.20 }}>
              {token ? (
                <button className="logout-button" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>Logout</button>
              ) : (
                <Link to="/login"><button className="logout-button" onClick={() => setMobileMenuOpen(false)}>Login</button></Link>
              )}
            </motion.div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Desktop nav - always visible */}
      <nav className="nav-links nav-links-desktop">
        <Link to="/catalog" className={isActive('/catalog') ? 'active' : ''}>Shop</Link>
        <Link to="/about" className={isActive('/about') ? 'active' : ''}>About Us</Link>
        <Link to="/contact" className={isActive('/contact') ? 'active' : ''}>Contact</Link>
        {token && <Link to="/orders" className={isActive('/orders') ? 'active' : ''}>Orders</Link>}
      </nav>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className={`header-search-bar ${mobileSearchOpen ? 'mobile-search-open' : ''}`} ref={searchDropdownRef}>
        <FaSearch style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#999', fontSize: '0.9rem', pointerEvents: 'none', zIndex: 1 }} />
        <input
          type="text"
          placeholder="Search for products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={(e) => {
            e.target.style.borderColor = '#7a9c3a';
            if (searchQuery.trim().length >= 2) setShowSearchDropdown(true);
          }}
          onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
          style={{
            width: '100%',
            padding: '10px 14px 10px 40px',
            border: '1px solid #e0e0e0',
            borderRadius: '25px',
            fontSize: '0.9rem',
            outline: 'none',
            transition: 'border-color 0.2s',
          }}
        />
        
        {/* Search Dropdown */}
        <AnimatePresence>
          {showSearchDropdown && (
            <motion.div 
              className="search-dropdown"
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
              {searchResults.length > 0 ? (
                <>
                {searchResults.map((product) => (
                  <div
                    key={product._id}
                    className="search-item"
                    onClick={() => handleProductClick(product._id)}
                  >
                    <img
                      src={product.image || '/default-plant.png'}
                      alt={product.name}
                      style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#2d3436', fontSize: '0.95rem' }}>{product.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#636e72' }}>
                        ₱{product.discount > 0 ? (
                          <>
                            <span style={{ fontWeight: 600, color: '#27ae60' }}>
                              {(product.price * (1 - product.discount / 100)).toFixed(2)}
                            </span>
                            <span style={{ textDecoration: 'line-through', marginLeft: '6px', color: '#95a5a6' }}>
                              {product.price.toFixed(2)}
                            </span>
                          </>
                        ) : (
                          <span style={{ fontWeight: 600 }}>{product.price.toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {searchResults.length === 8 && (
                  <div
                    className="search-item-view-all"
                    onClick={() => {
                      navigate(`/catalog?search=${encodeURIComponent(searchQuery.trim())}`);
                      setShowSearchDropdown(false);
                    }}
                  >
                    View all results →
                  </div>
                )}
              </>
            ) : (
              <div className="search-no-results">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{ marginBottom: '8px', opacity: 0.3 }}>
                  <path d="M20 21l-4.35-4.35M18 11a7 7 0 11-14 0 7 7 0 0114 0z" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 11h6M11 8v6" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <div style={{ fontWeight: 600, color: '#555', fontSize: '0.95rem', marginBottom: '4px' }}>No products found</div>
                <div style={{ fontSize: '0.85rem', color: '#888' }}>Try a different search term</div>
              </div>
            )}
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      <div className="icons" ref={dropdownRef}>
        <FaSearch 
          className="icon mobile-search-toggle" 
          onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
        />
        <div className="cart-icon-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <FaShoppingCart className="icon" onClick={() => token ? navigate('/cart') : setShowAuthPrompt(true)} />
          {cartCount > 0 && (
            <span className="cart-badge">{cartCount}</span>
          )}
        </div>
        <div className="notif-icon-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <FaBell className="icon" onClick={() => token ? setShowDropdown(s => !s) : setShowAuthPrompt(true)} />
          {unreadCount > 0 && (
            <span className="notif-badge">{unreadCount}</span>
          )}
          {showDropdown && (
            <div className="notif-dropdown">
              <div className="notif-header">
                <div className="notif-title">
                  Notifications
                </div>
                {unreadCount > 0 && (
                  <button
                    className="notif-mark-all"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        // Optimistically mark all as read client-side
                        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                        // Batch mark each unread as read
                        const unread = notifications.filter(n => !n.read);
                        await Promise.all(unread.map(n => axios.patch(`${process.env.REACT_APP_API}/api/users/notifications/${n._id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } })));
                      } catch (err) {
                        // ignore errors; UI remains updated
                      }
                    }}
                    aria-label="Mark all notifications as read"
                  >
                    Mark all as read
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="notif-empty">No notifications</div>
              ) : (
                <div className="notif-list">
                  {notifications.map(n => (
                    <div key={n._id} className={`notif-item ${n.read ? 'read' : 'unread'}`} onClick={async () => {
                        await markAsRead(n);
                        // message_reply is handled in markAsRead, don't open generic modal
                        if (n.type === 'message_reply') {
                          return;
                        }
                        if (n.type === 'admin_message' || n.body) {
                          setModalPayload({ 
                            title: n.title || n.message, 
                            body: n.body || n.message || '', 
                            from: (n.type === 'admin_message' ? "Chiqui's Garden" : (n.meta && n.meta.from) || ''),
                            meta: n.meta || null
                          });
                          setModalOpen(true);
                        } else if (n.type === 'points') {
                          navigate('/profile');
                        } else if (n.type === 'promotion') {
                          setModalPayload({
                            title: n.title || 'New Promotion',
                            body: n.body || n.message || '',
                            from: "Chiqui's Garden",
                            meta: n.meta || null
                          });
                          setModalOpen(true);
                        } else if (n.type === 'order_expired' && n.meta && n.meta.orderId) {
                          navigate(`/orders?open=${encodeURIComponent(n.meta.orderId)}`);
                        }
                      }}>
                      <div className="notif-item-inner">
                        <div>
                          <div className="notif-message">{n.title || n.message}</div>
                          {n.type === 'admin_message' && <div className="notif-from-inline">From: Chiqui's Garden</div>}
                        </div>
                        <div className="notif-time">{new Date(n.createdAt).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <FaUser className="icon" onClick={() => token ? navigate('/profile') : setShowAuthPrompt(true)} />
        {token ? (
          <svg 
            className="icon auth-icon" 
            onClick={handleLogout}
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            style={{ cursor: 'pointer' }}
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
        ) : (
          <svg 
            className="icon auth-icon" 
            onClick={() => navigate('/login')}
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            style={{ cursor: 'pointer' }}
          >
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
            <polyline points="10 17 15 12 10 7"></polyline>
            <line x1="15" y1="12" x2="3" y2="12"></line>
          </svg>
        )}
      </div>
      <NotificationModal open={modalOpen} onClose={() => setModalOpen(false)} title={modalPayload.title} body={modalPayload.body} from={modalPayload.from} meta={modalPayload.meta} />
      <AuthPromptModal open={showAuthPrompt} onClose={() => setShowAuthPrompt(false)} message={"Please login to continue. You can login or go back."} />
      
      {/* Message Reply Modal - Rendered as Portal */}
      {showMessageModal && selectedMessageNotif && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100000,
              padding: '20px',
              overflow: 'hidden'
            }}
            onClick={() => setShowMessageModal(false)}>
            
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '85vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                position: 'relative'
              }}>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'start',
                marginBottom: '24px',
                paddingBottom: '16px',
                borderBottom: '2px solid #f1f5f9'
              }}>
                <div>
                  <h2 style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 700, 
                    color: '#1e293b',
                    margin: '0 0 8px 0'
                  }}>
                    {messageDetails?.subject || selectedMessageNotif.meta?.subject || selectedMessageNotif.title || 'Reply to Your Message'}
                  </h2>
                  <p style={{ 
                    fontSize: '0.9rem', 
                    color: '#94a3b8',
                    margin: 0
                  }}>
                    {new Date(selectedMessageNotif.createdAt || selectedMessageNotif.timestamp || new Date()).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <button
                  onClick={() => setShowMessageModal(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '1.5rem',
                    color: '#64748b',
                    cursor: 'pointer',
                    padding: '4px 8px'
                  }}>
                  ×
                </button>
              </div>

              {/* Latest Reply from Admin */}
              {loadingMessageDetails ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ 
                    display: 'inline-block',
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #7a9c3a',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <p style={{ color: '#64748b', marginTop: '16px' }}>Loading conversation...</p>
                  <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); }}`}</style>
                </div>
              ) : messageDetails?.replies && messageDetails.replies.length > 0 ? (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ 
                    fontSize: '0.9rem', 
                    fontWeight: 600, 
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    Latest Reply from Admin
                  </h3>
                  <div style={{
                    background: '#f8fdf9',
                    padding: '20px',
                    borderRadius: '8px',
                    borderLeft: '4px solid #7a9c3a'
                  }}>
                    <p style={{ 
                      fontSize: '1rem', 
                      color: '#334155',
                      lineHeight: 1.6,
                      margin: 0,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {messageDetails.replies[messageDetails.replies.length - 1].text}
                    </p>
                    <p style={{ 
                      fontSize: '0.8rem', 
                      color: '#94a3b8',
                      marginTop: '12px',
                      marginBottom: 0
                    }}>
                      {new Date(messageDetails.replies[messageDetails.replies.length - 1].sentAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ 
                    fontSize: '0.9rem', 
                    fontWeight: 600, 
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    Latest Reply from Admin
                  </h3>
                  <div style={{
                    background: '#f8fdf9',
                    padding: '20px',
                    borderRadius: '8px',
                    borderLeft: '4px solid #7a9c3a'
                  }}>
                    <p style={{ 
                      fontSize: '1rem', 
                      color: '#334155',
                      lineHeight: 1.6,
                      margin: 0,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {selectedMessageNotif.body || selectedMessageNotif.message || 'Admin has replied to your message.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Original Message */}
              {!loadingMessageDetails && (messageDetails?.message || selectedMessageNotif.meta?.originalMessage) && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ 
                    fontSize: '0.9rem', 
                    fontWeight: 600, 
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    Your Original Message
                  </h3>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '16px',
                    borderRadius: '8px',
                    borderLeft: '3px solid #94a3b8'
                  }}>
                    <p style={{ 
                      fontSize: '0.95rem', 
                      color: '#475569',
                      lineHeight: 1.6,
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      fontStyle: 'italic'
                    }}>
                      {messageDetails?.message || selectedMessageNotif.meta?.originalMessage || 'Your message content'}
                    </p>
                  </div>
                </div>
              )}

              {/* Conversation History */}
              {!loadingMessageDetails && ((messageDetails?.replies && messageDetails.replies.length > 1) || (selectedMessageNotif.meta?.replies && selectedMessageNotif.meta.replies.length > 1)) && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ 
                    fontSize: '0.9rem', 
                    fontWeight: 600, 
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    Previous Replies ({(messageDetails?.replies?.length || selectedMessageNotif.meta?.replies?.length || 1) - 1})
                  </h3>
                  <div style={{
                    background: '#fafafa',
                    padding: '16px',
                    borderRadius: '8px',
                    maxHeight: '250px',
                    overflowY: 'auto'
                  }}>
                    {((messageDetails?.replies || selectedMessageNotif.meta?.replies || []).slice(0, -1).reverse()).map((reply, idx, arr) => (
                      <div key={idx} style={{
                        padding: '12px',
                        background: 'white',
                        borderRadius: '6px',
                        marginBottom: idx < arr.length - 1 ? '12px' : '0',
                        borderLeft: reply.sentBy === 'Admin' ? '3px solid #7a9c3a' : '3px solid #cbd5e1'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <span style={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: reply.sentBy === 'Admin' ? '#7a9c3a' : '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {reply.sentBy}
                          </span>
                          <span style={{
                            fontSize: '0.75rem',
                            color: '#94a3b8'
                          }}>
                            {new Date(reply.sentAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <p style={{
                          fontSize: '0.9rem',
                          color: '#475569',
                          lineHeight: 1.5,
                          margin: 0,
                          whiteSpace: 'pre-wrap'
                        }}>
                          {reply.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{
                background: '#f1f5f9',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <p style={{ 
                  fontSize: '0.9rem', 
                  color: '#64748b',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>📧</span>
                  A detailed reply has been sent to your email address.
                </p>
              </div>

              <motion.button
                onClick={() => setShowMessageModal(false)}
                whileHover={{ scale: 1.02, backgroundColor: '#5e7c2a' }}
                whileTap={{ scale: 0.98 }}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: '#7a9c3a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '1rem',
                  boxShadow: '0 2px 8px rgba(122,156,58,0.08)'
                }}>
                Close
              </motion.button>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}

export default DashboardHeader;
